import { usePortalNotifications } from '@/context/Notifications';
import { usePageModels } from '@/context/PageModels';
import { MuiButton, MuiTypography } from '@/features/shared/material-ui';
import { useDataLayerOneTrustConfirmationEvent } from '@/hooks/useDataLayerEvent';
import { replaceInText } from '@/utils/text';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { isUndefined } from 'lodash';
import { getOr, isEmpty } from 'lodash/fp';
import React, { memo, useEffect, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import NotificationCard from './components/NotificationCard';
import { Notification, NotificationProps } from './Notifications.interfaces';
import useNotificationStyles from './Notifications.styles';
import { sortByFeatured } from './Notifications.utils';

enum NotificationLabelsKeys {
  mainKey = 'portalNotifications',
  heading = 'notification_dropdown_heading_label',
  markAllAsRead = 'mark_all_as_read_label',
  unreadCount = 'unread_count_label',
  emptyNotifications = 'empty_notifications_label',
}

const Notifications = ({ notifications = [], unreadCount }: NotificationProps) => {
  const classes = useNotificationStyles();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { labels } = usePageModels();
  const [localNotifications, setLocalNotifications] = useState<Notification[]>([]);
  const hasLoadUnreadNotificationImpression = React.useRef(false);
  const [isOneTrustCookieAccepted, setIsOneTrustCookieAccepted] = useState<boolean>(false);

  const notificationLabels = useMemo(
    () => getOr({}, NotificationLabelsKeys.mainKey, labels),
    [labels],
  );

  const { markAllAsRead } = usePortalNotifications();

  useDataLayerOneTrustConfirmationEvent(
    (_, unsubscribe = () => {}) => {
      setIsOneTrustCookieAccepted(true);
      unsubscribe();
    },
    { fireOnMount: true },
  );

  useEffect(() => {
    const modifiedNotifications = sortByFeatured(notifications);
    setLocalNotifications(modifiedNotifications);
  }, [notifications, setLocalNotifications]);

  const handleMarkAllReadClick = React.useCallback(
    async (e: any) => {
      e.stopPropagation();
      TagManager.dataLayer({
        dataLayer: {
          event: 'trackEvent-notificationEngagement',
          event_name: 'click_notification_mark_read_all',
          count: unreadCount?.toString(),
          position: 'notification_tray',
        },
      });
      await markAllAsRead();
    },
    [markAllAsRead, unreadCount],
  );

  const showNotificationInfo = useMemo(
    () => !isEmpty(notifications) && !!unreadCount,
    [notifications, unreadCount],
  );

  // After Page load, if unreadCount is available, then send impression event.
  React.useEffect(() => {
    if (isUndefined(unreadCount)) return;
    if (hasLoadUnreadNotificationImpression.current) return;
    if (!isOneTrustCookieAccepted) return;

    hasLoadUnreadNotificationImpression.current = true;
    TagManager.dataLayer({
      dataLayer: {
        event: 'trackEvent-notificationEngagement',
        event_name: 'unread_notification_impression',
        count: unreadCount?.toString(),
        position: 'notification_tray',
      },
    });
  }, [unreadCount, isOneTrustCookieAccepted]);

  // Click event for notification bell
  const handleClickBell = React.useCallback(() => {
    if (isUndefined(unreadCount)) return;

    TagManager.dataLayer({
      dataLayer: {
        event: 'trackEvent-notificationEngagement',
        event_name: 'click_notification_bell',
        count: unreadCount?.toString(),
        position: 'notification_tray',
      },
    });
  }, [unreadCount]);

  const handleClickNotification = React.useCallback((notification: Notification, index: number) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'trackEvent-notificationEngagement',
        event_name: 'click_notification',
        title: notification.heading,
        link_url: notification.link.href,
        type: '',
        position: 'notification_tray',
        index_num: index?.toString(),
      },
    });
  }, []);

  React.useEffect(() => {
    document.addEventListener('notifications:on-opening-click', handleClickBell);
    return () => document.removeEventListener('notifications:on-opening-click', handleClickBell);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadCount]);

  const NotificationInfo = () => (
    <div className={clsx({ [classes.infoMobile]: isMobile })}>
      <MuiTypography variant="body1" color="inherit">
        {replaceInText(
          [unreadCount?.toString()],
          notificationLabels[NotificationLabelsKeys.unreadCount] || '%1 unread',
        )}
      </MuiTypography>
      <MuiButton
        variant="text"
        color="secondary"
        className="notification-mark-as-read"
        onClick={handleMarkAllReadClick}
      >
        {notificationLabels[NotificationLabelsKeys.markAllAsRead] ?? 'Mark all as read'}
      </MuiButton>
    </div>
  );

  return (
    <div
      data-testid="notifications-container"
      data-datalayer-ignore-link-section
      data-datalayer-position="notification_tray"
    >
      <div className={classes.notificationHeader}>
        <div>
          <MuiTypography variant="h6" component="h6" className="notification-heading">
            {notificationLabels[NotificationLabelsKeys.heading]}
          </MuiTypography>
          {isMobile && showNotificationInfo && <NotificationInfo />}
        </div>
        {!isMobile && showNotificationInfo && <NotificationInfo />}
      </div>
      {isEmpty(notifications) ? (
        <div className={classes.emptyNotification}>
          <MuiTypography variant="body1" component="p">
            {notificationLabels[NotificationLabelsKeys.emptyNotifications] || 'No notifications'}
          </MuiTypography>
        </div>
      ) : (
        <div data-testid="notification-list" className={classes.notificationList}>
          {localNotifications.map((notification, index) => (
            <div className="notification" key={notification.id}>
              <NotificationCard
                key={notification.id}
                isUnread={!notification.read}
                notification={notification}
                notificationsLabels={getOr({}, NotificationLabelsKeys.mainKey, labels)}
                onClickNotification={() => handleClickNotification(notification, index)}
              />
              <span className="divider" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(Notifications);
